import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const PrivacyPolicyPage = () => {
  const [pageVisible, setPageVisible] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Privacy Policy — Insust Global';
    const t = setTimeout(() => setPageVisible(true), 50);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className={`min-h-screen bg-slate-950 transition-opacity duration-700 ${pageVisible ? 'opacity-100' : 'opacity-0'}`}>
      <nav className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 lg:px-12 h-16 bg-slate-950/90 backdrop-blur-lg border-b border-slate-800/50">
        <Link to="/" className="flex items-center gap-3">
          <img src="/logo.jpeg" alt="Insust Global logo" className="h-8 w-8 rounded-full object-contain" />
          <span className="text-lg font-bold text-white">Insust Global</span>
        </Link>
        <Link to="/" className="flex items-center gap-2 text-sm text-slate-400 hover:text-teal-400 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>
      </nav>

      <div className="pt-28 pb-20 px-6 lg:px-12">
        <div className="max-w-[780px] mx-auto">
          <span className="text-xs font-mono tracking-[0.2em] uppercase text-teal-400 block mb-4">Legal</span>
          <h1 className="text-4xl font-bold text-white mb-2">Privacy Policy</h1>
          <p className="text-sm text-slate-500 mb-12">Last updated: March 2026</p>

          <div className="prose-sm space-y-8 text-slate-400 leading-relaxed">
            <section>
              <h2 className="text-lg font-bold text-white mb-3">1. Information We Collect</h2>
              <p>We collect information you provide directly when using our services, including:</p>
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>Name, email address, and phone number when you submit forms or enroll in courses</li>
                <li>Professional details such as qualification and experience level</li>
                <li>Messages and inquiries submitted through contact or demo request forms</li>
                <li>Usage data and analytics collected through cookies and similar technologies</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-bold text-white mb-3">2. How We Use Your Information</h2>
              <p>We use collected information to:</p>
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>Process course enrollments and communicate with you about your enrollment</li>
                <li>Respond to demo requests and inquiries</li>
                <li>Improve our products, services, and website experience</li>
                <li>Send relevant updates about courses, products, and services (with your consent)</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-bold text-white mb-3">3. Data Sharing</h2>
              <p>We do not sell your personal information. We may share data with:</p>
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>Service providers who assist in operating our platform (hosting, analytics, email)</li>
                <li>Legal authorities when required by applicable law</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-bold text-white mb-3">4. Cookies & Analytics</h2>
              <p>We use cookies and analytics tools (including PostHog) to understand how visitors interact with our website. These help us improve user experience and measure the effectiveness of our content. You may disable cookies through your browser settings.</p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-white mb-3">5. Data Security</h2>
              <p>We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet is 100% secure.</p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-white mb-3">6. Your Rights</h2>
              <p>You have the right to:</p>
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>Access, correct, or delete your personal data</li>
                <li>Withdraw consent for marketing communications</li>
                <li>Request a copy of the data we hold about you</li>
              </ul>
              <p className="mt-2">To exercise these rights, contact us at <a href="mailto:privacy@insust.in" className="text-teal-400 hover:underline">privacy@insust.in</a>.</p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-white mb-3">7. Changes to This Policy</h2>
              <p>We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the updated policy on this page with a revised "Last updated" date.</p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-white mb-3">8. Contact</h2>
              <p>For questions about this Privacy Policy, contact us at:<br />
                <a href="mailto:privacy@insust.in" className="text-teal-400 hover:underline">privacy@insust.in</a>
              </p>
              <p className="mt-2">Insust Global</p>
            </section>
          </div>
        </div>
      </div>

      <footer className="bg-slate-950 border-t border-slate-800 py-6 px-6 lg:px-16 flex flex-col sm:flex-row items-center justify-between gap-4">
        <span className="text-xs text-slate-600">&copy; {new Date().getFullYear()} Insust Global. All rights reserved.</span>
        <div className="flex gap-4 text-xs text-slate-600">
          <Link to="/privacy" className="text-teal-400/60">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-teal-400 transition-colors">Terms of Service</Link>
          <Link to="/" className="hover:text-teal-400 transition-colors">Home</Link>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicyPage;
